import { useState, useEffect, useCallback, useRef } from "react";
import {
  ShieldCheck, Key, FileSignature, Copy, CheckCircle2, ChevronDown, ChevronUp,
  Loader2, Vault, Trash2, RefreshCw, Save, AlertCircle, BarChart2,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { tokenStore } from "@/lib/api";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  return { "Content-Type": "application/json", Authorization: `Bearer ${tokenStore.getAccess()}` };
}

async function apiPost(path, body) {
  const res = await fetch(`${API}/api${path}`, { method: "POST", headers: authHeaders(), body: JSON.stringify(body) });
  const d = await res.json();
  if (!res.ok) throw new Error(d?.detail || d?.error?.message || "Request failed");
  return d;
}

async function apiGet(path) {
  const res = await fetch(`${API}/api${path}`, { headers: authHeaders() });
  const d = await res.json();
  if (!res.ok) throw new Error(d?.detail || d?.error?.message || "Request failed");
  return d;
}

async function apiDelete(path) {
  const res = await fetch(`${API}/api${path}`, { method: "DELETE", headers: authHeaders() });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d?.detail || "Delete failed");
  }
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
      title="Copy to clipboard"
    >
      {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
}

function CodeBlock({ label, value }) {
  const [expanded, setExpanded] = useState(false);
  if (!value) return null;
  const short = value.length > 80 ? value.slice(0, 80) + "..." : value;
  return (
    <div className="mt-2">
      <div className="flex items-center justify-between mb-0.5">
        <span className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">{label}</span>
        <div className="flex items-center gap-1">
          <CopyButton text={value} />
          {value.length > 80 && (
            <button onClick={() => setExpanded(e => !e)} className="text-[10px] text-blue-500 hover:underline flex items-center gap-0.5">
              {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {expanded ? "less" : "more"}
            </button>
          )}
        </div>
      </div>
      <pre className="text-[10px] font-mono bg-slate-950 text-emerald-400 rounded p-2 overflow-x-auto whitespace-pre-wrap break-all">
        {expanded ? value : short}
      </pre>
    </div>
  );
}

// ── ML-KEM Panel ──────────────────────────────────────────────────────────────

function MLKEMPanel({ onSaved }) {
  const [keys, setKeys]         = useState(null);
  const [capsule, setCapsule]   = useState(null);
  const [decapped, setDecapped] = useState(null);
  const [loading, setLoading]   = useState("");
  const [keyName, setKeyName]   = useState("");
  const [saving, setSaving]     = useState(false);

  const doKeygen = async () => {
    setLoading("keygen"); setKeys(null); setCapsule(null); setDecapped(null); setKeyName("");
    try { setKeys(await apiPost("/v1/pqc/kem/keygen", {})); }
    catch (e) { toast.error(e.message); }
    setLoading("");
  };

  const doEncapsulate = async () => {
    if (!keys) return;
    setLoading("encaps"); setCapsule(null); setDecapped(null);
    try { setCapsule(await apiPost("/v1/pqc/kem/encapsulate", keys.encapsulation_key)); }
    catch (e) { toast.error(e.message); }
    setLoading("");
  };

  const doDecapsulate = async () => {
    if (!keys || !capsule) return;
    setLoading("decaps"); setDecapped(null);
    try { setDecapped(await apiPost("/v1/pqc/kem/decapsulate", { decapsulation_key: keys.decapsulation_key, ciphertext: capsule.ciphertext })); }
    catch (e) { toast.error(e.message); }
    setLoading("");
  };

  const doSaveToVault = async () => {
    if (!keys) return;
    setSaving(true);
    try {
      await apiPost("/v1/pqc/vault/keys", {
        algorithm: "ML-KEM-768",
        name: keyName.trim() || "ML-KEM-768 key",
        public_key: keys.encapsulation_key,
        private_key: keys.decapsulation_key,
        extra: { encapsulation_key: keys.encapsulation_key, decapsulation_key: keys.decapsulation_key, ek_size_bytes: keys.ek_size_bytes, dk_size_bytes: keys.dk_size_bytes },
      });
      toast.success("Key pair saved to vault");
      onSaved?.();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const bothMatch = decapped && capsule && decapped.shared_secret === capsule.shared_secret;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="kem-panel">
      <div className="flex items-center gap-2 mb-4">
        <Key className="w-5 h-5 text-blue-600" />
        <div>
          <h2 className="text-base font-semibold text-slate-800">ML-KEM-768 (FIPS 203)</h2>
          <p className="text-xs text-slate-500">Key Encapsulation Mechanism — replaces RSA/ECDH key exchange</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={doKeygen} disabled={!!loading} className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5" data-testid="kem-keygen-btn">
          {loading === "keygen" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Key className="w-3.5 h-3.5" />}
          1. Generate Key Pair
        </button>
        <button onClick={doEncapsulate} disabled={!keys || !!loading} className="px-3 py-1.5 bg-slate-700 text-white text-xs font-medium rounded-lg hover:bg-slate-800 disabled:opacity-50 flex items-center gap-1.5" data-testid="kem-encaps-btn">
          {loading === "encaps" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
          2. Encapsulate
        </button>
        <button onClick={doDecapsulate} disabled={!capsule || !!loading} className="px-3 py-1.5 bg-slate-700 text-white text-xs font-medium rounded-lg hover:bg-slate-800 disabled:opacity-50 flex items-center gap-1.5" data-testid="kem-decaps-btn">
          {loading === "decaps" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
          3. Decapsulate
        </button>
      </div>
      {keys && (
        <>
          <div className="space-y-1">
            <CodeBlock label={`Encapsulation Key (${keys.ek_size_bytes}B)`} value={keys.encapsulation_key} />
            <CodeBlock label={`Decapsulation Key (${keys.dk_size_bytes}B)`} value={keys.decapsulation_key} />
          </div>
          <div className="mt-3 flex items-center gap-2">
            <input
              value={keyName}
              onChange={e => setKeyName(e.target.value)}
              placeholder="Key name (optional)"
              className="flex-1 text-xs border border-slate-200 rounded px-2 py-1.5 bg-slate-50 focus:outline-none focus:ring-1 focus:ring-blue-300"
              data-testid="kem-key-name-input"
            />
            <button
              onClick={doSaveToVault}
              disabled={saving}
              className="px-3 py-1.5 bg-emerald-600 text-white text-xs font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1.5"
              data-testid="kem-save-vault-btn"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Save to Vault
            </button>
          </div>
        </>
      )}
      {capsule && (
        <div className="mt-2 space-y-1">
          <CodeBlock label={`Ciphertext (${capsule.ct_size_bytes}B)`} value={capsule.ciphertext} />
          <CodeBlock label={`Shared Secret — Sender (${capsule.ss_size_bytes}B)`} value={capsule.shared_secret} />
        </div>
      )}
      {decapped && (
        <div className="mt-2">
          <CodeBlock label={`Shared Secret — Recipient (${decapped.ss_size_bytes}B)`} value={decapped.shared_secret} />
          {bothMatch !== null && (
            <div className={`mt-2 flex items-center gap-2 text-sm font-semibold ${bothMatch ? "text-emerald-600" : "text-red-600"}`} data-testid="kem-match-result">
              <CheckCircle2 className="w-4 h-4" />
              {bothMatch ? "Shared secrets match — key exchange successful!" : "Mismatch — key exchange failed"}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── ML-DSA Panel ──────────────────────────────────────────────────────────────

function MLDSAPanel({ onSaved }) {
  const [keys, setKeys]         = useState(null);
  const [sig, setSig]           = useState(null);
  const [verified, setVerified] = useState(null);
  const [msg, setMsg]           = useState("QCoreSecure quantum-safe signature demo");
  const [loading, setLoading]   = useState("");
  const [keyName, setKeyName]   = useState("");
  const [saving, setSaving]     = useState(false);

  const doKeygen = async () => {
    setLoading("keygen"); setKeys(null); setSig(null); setVerified(null); setKeyName("");
    try { setKeys(await apiPost("/v1/pqc/dsa/keygen", {})); }
    catch (e) { toast.error(e.message); }
    setLoading("");
  };

  const doSign = async () => {
    if (!keys) return;
    setLoading("sign"); setSig(null); setVerified(null);
    try { setSig(await apiPost("/v1/pqc/dsa/sign", { private_key: keys.private_key, message: msg })); }
    catch (e) { toast.error(e.message); }
    setLoading("");
  };

  const doVerify = async () => {
    if (!keys || !sig) return;
    setLoading("verify"); setVerified(null);
    try { setVerified(await apiPost("/v1/pqc/dsa/verify", { public_key: keys.public_key, message: msg, signature: sig.signature })); }
    catch (e) { toast.error(e.message); }
    setLoading("");
  };

  const doSaveToVault = async () => {
    if (!keys) return;
    setSaving(true);
    try {
      await apiPost("/v1/pqc/vault/keys", {
        algorithm: "ML-DSA-65",
        name: keyName.trim() || "ML-DSA-65 key",
        public_key: keys.public_key,
        private_key: keys.private_key,
        extra: { pk_size_bytes: keys.pk_size_bytes, sk_size_bytes: keys.sk_size_bytes },
      });
      toast.success("Key pair saved to vault");
      onSaved?.();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="dsa-panel">
      <div className="flex items-center gap-2 mb-4">
        <FileSignature className="w-5 h-5 text-violet-600" />
        <div>
          <h2 className="text-base font-semibold text-slate-800">ML-DSA-65 (FIPS 204)</h2>
          <p className="text-xs text-slate-500">Digital Signature — replaces ECDSA/RSA signatures</p>
        </div>
      </div>
      <div className="mb-3">
        <label className="text-xs text-slate-500 block mb-1">Message to sign</label>
        <input value={msg} onChange={e => setMsg(e.target.value)} className="w-full text-xs border border-slate-200 rounded px-2 py-1.5 font-mono bg-slate-50 focus:outline-none focus:ring-1 focus:ring-violet-300" data-testid="dsa-message-input" />
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={doKeygen} disabled={!!loading} className="px-3 py-1.5 bg-violet-600 text-white text-xs font-medium rounded-lg hover:bg-violet-700 disabled:opacity-50 flex items-center gap-1.5" data-testid="dsa-keygen-btn">
          {loading === "keygen" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Key className="w-3.5 h-3.5" />}
          1. Generate Key Pair
        </button>
        <button onClick={doSign} disabled={!keys || !!loading} className="px-3 py-1.5 bg-slate-700 text-white text-xs font-medium rounded-lg hover:bg-slate-800 disabled:opacity-50 flex items-center gap-1.5" data-testid="dsa-sign-btn">
          {loading === "sign" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
          2. Sign
        </button>
        <button onClick={doVerify} disabled={!sig || !!loading} className="px-3 py-1.5 bg-slate-700 text-white text-xs font-medium rounded-lg hover:bg-slate-800 disabled:opacity-50 flex items-center gap-1.5" data-testid="dsa-verify-btn">
          {loading === "verify" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
          3. Verify
        </button>
      </div>
      {keys && (
        <>
          <div className="space-y-1">
            <CodeBlock label={`Public Key (${keys.pk_size_bytes}B)`} value={keys.public_key} />
            <CodeBlock label={`Private Key (${keys.sk_size_bytes}B)`} value={keys.private_key} />
          </div>
          <div className="mt-3 flex items-center gap-2">
            <input
              value={keyName}
              onChange={e => setKeyName(e.target.value)}
              placeholder="Key name (optional)"
              className="flex-1 text-xs border border-slate-200 rounded px-2 py-1.5 bg-slate-50 focus:outline-none focus:ring-1 focus:ring-violet-300"
              data-testid="dsa-key-name-input"
            />
            <button
              onClick={doSaveToVault}
              disabled={saving}
              className="px-3 py-1.5 bg-emerald-600 text-white text-xs font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1.5"
              data-testid="dsa-save-vault-btn"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Save to Vault
            </button>
          </div>
        </>
      )}
      {sig && <CodeBlock label={`Signature (${sig.sig_size_bytes}B)`} value={sig.signature} />}
      {verified !== null && (
        <div className={`mt-3 flex items-center gap-2 text-sm font-semibold ${verified.valid ? "text-emerald-600" : "text-red-600"}`} data-testid="dsa-verify-result">
          <CheckCircle2 className="w-4 h-4" />
          {verified.valid ? "Signature valid — message authenticated!" : "Signature INVALID"}
        </div>
      )}
    </div>
  );
}

// ── SLH-DSA Panel ─────────────────────────────────────────────────────────────

const SLH_DSA_VARIANT_INFO = {
  "128s": {
    label:     "128s — Standard",
    algo:      "SLH-DSA-SHAKE-128s",
    badge:     "NIST L1",
    sigSize:   "~7.9 KB",
    pkBytes:   "32 B", skBytes: "64 B",
    estMs:     900,
    color:     "bg-blue-600",
    hoverColor:"hover:bg-blue-700",
    textColor: "text-blue-700",
    bgColor:   "bg-blue-50",
    border:    "border-blue-200",
    badgeColor:"bg-blue-100 text-blue-700 border-blue-200",
    note:      "Recommended — smaller signatures (~7.9 KB) and faster signing.",
  },
  "256s": {
    label:     "256s — Max Security",
    algo:      "SLH-DSA-SHAKE-256s",
    badge:     "NIST L5",
    sigSize:   "~29 KB",
    pkBytes:   "64 B", skBytes: "128 B",
    estMs:     1400,
    color:     "bg-emerald-600",
    hoverColor:"hover:bg-emerald-700",
    textColor: "text-emerald-700",
    bgColor:   "bg-emerald-50",
    border:    "border-emerald-200",
    badgeColor:"bg-emerald-100 text-emerald-700 border-emerald-200",
    note:      "Maximum quantum security — use when long-term 256-bit security is required.",
  },
};

function SignProgress({ elapsedMs, variant }) {
  const info    = SLH_DSA_VARIANT_INFO[variant] || SLH_DSA_VARIANT_INFO["256s"];
  const estMs   = info.estMs;
  const pct     = Math.min(97, Math.round((elapsedMs / estMs) * 100));
  const elapsedSec = (elapsedMs / 1000).toFixed(1);
  return (
    <div className="mt-2 space-y-1.5" data-testid="slhdsa-sign-progress">
      <div className="flex items-center justify-between text-xs">
        <span className={`font-medium ${info.textColor}`}>Signing with {info.algo}...</span>
        <span className="font-mono text-slate-500">{elapsedSec}s elapsed</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div
          className={`h-full ${info.color} rounded-full transition-all`}
          style={{ width: `${pct}%`, transition: "width 0.1s linear" }}
        />
      </div>
      <p className="text-[10px] text-slate-400">
        Estimated: ~{(info.estMs / 1000).toFixed(1)}s — {info.sigSize} signature size
      </p>
    </div>
  );
}

function SLHDSAPanel({ onSaved }) {
  const [variant, setVariant]   = useState("256s");
  const [keys, setKeys]         = useState(null);
  const [sig, setSig]           = useState(null);
  const [verified, setVerified] = useState(null);
  const [msg, setMsg]           = useState("QCoreSecure SLH-DSA signature demo");
  const [loading, setLoading]   = useState("");
  const [elapsedMs, setElapsedMs] = useState(0);
  const [keyName, setKeyName]   = useState("");
  const [saving, setSaving]     = useState(false);
  const timerRef                = useRef(null);

  const info = SLH_DSA_VARIANT_INFO[variant] || SLH_DSA_VARIANT_INFO["256s"];

  // Start/stop elapsed timer when sign is in progress
  useEffect(() => {
    if (loading === "sign") {
      setElapsedMs(0);
      timerRef.current = setInterval(() => setElapsedMs(ms => ms + 100), 100);
    } else {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    return () => clearInterval(timerRef.current);
  }, [loading]);

  const handleVariantChange = (v) => {
    setVariant(v);
    setKeys(null); setSig(null); setVerified(null); setKeyName("");
  };

  const doKeygen = async () => {
    setLoading("keygen"); setKeys(null); setSig(null); setVerified(null); setKeyName("");
    try { setKeys(await apiPost("/v1/pqc/slhdsa/keygen", { variant })); }
    catch (e) { toast.error(e.message); }
    setLoading("");
  };

  const doSign = async () => {
    if (!keys) return;
    setLoading("sign"); setSig(null); setVerified(null);
    try { setSig(await apiPost("/v1/pqc/slhdsa/sign", { private_key: keys.private_key, message: msg, variant })); }
    catch (e) { toast.error(e.message); }
    setLoading("");
  };

  const doVerify = async () => {
    if (!keys || !sig) return;
    setLoading("verify"); setVerified(null);
    try { setVerified(await apiPost("/v1/pqc/slhdsa/verify", { public_key: keys.public_key, message: msg, signature: sig.signature, variant })); }
    catch (e) { toast.error(e.message); }
    setLoading("");
  };

  const doSaveToVault = async () => {
    if (!keys) return;
    setSaving(true);
    try {
      await apiPost("/v1/pqc/vault/keys", {
        algorithm:   info.algo,
        name:        keyName.trim() || `${info.algo} key`,
        public_key:  keys.public_key,
        private_key: keys.private_key,
        extra:       { pk_size_bytes: keys.pk_size_bytes, sk_size_bytes: keys.sk_size_bytes, sig_size_bytes: keys.sig_size_bytes },
      });
      toast.success("Key pair saved to vault");
      onSaved?.();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="slhdsa-panel">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className={`w-5 h-5 ${info.textColor}`} />
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-base font-semibold text-slate-800">SLH-DSA (FIPS 205 / SPHINCS+)</h2>
            <span className={`text-[9px] border px-1.5 py-0.5 rounded-full font-semibold uppercase ${info.badgeColor}`}>{info.badge}</span>
          </div>
          <p className="text-xs text-slate-500">Hash-Based Signature — no lattice assumptions, robust against harvest-now-decrypt-later attacks</p>
        </div>
      </div>

      {/* Variant selector */}
      <div className="flex gap-2 mt-3 mb-4" data-testid="slhdsa-variant-selector">
        {Object.entries(SLH_DSA_VARIANT_INFO).map(([key, v]) => (
          <button
            key={key}
            onClick={() => handleVariantChange(key)}
            className={`flex-1 py-2 px-3 rounded-lg border text-xs font-medium transition-all ${
              variant === key
                ? `${v.color} text-white border-transparent shadow-sm`
                : "border-slate-200 text-slate-600 hover:border-slate-300 bg-white"
            }`}
            data-testid={`variant-btn-${key}`}
          >
            <div className="font-semibold">{v.label}</div>
            <div className={`text-[10px] mt-0.5 ${variant === key ? "text-white/80" : "text-slate-400"}`}>
              sig: {v.sigSize} · pk: {v.pkBytes}
            </div>
          </button>
        ))}
      </div>
      <p className={`text-[10px] ${info.textColor} ${info.bgColor} border ${info.border} rounded px-2 py-1 mb-4`}>
        {info.note}
      </p>

      <div className="mb-3">
        <label className="text-xs text-slate-500 block mb-1">Message to sign</label>
        <input
          value={msg}
          onChange={e => setMsg(e.target.value)}
          className="w-full text-xs border border-slate-200 rounded px-2 py-1.5 font-mono bg-slate-50 focus:outline-none focus:ring-1 focus:ring-slate-300"
          data-testid="slhdsa-message-input"
        />
      </div>

      <div className="flex flex-wrap gap-2 mb-2">
        <button
          onClick={doKeygen}
          disabled={!!loading}
          className={`px-3 py-1.5 ${info.color} ${info.hoverColor} text-white text-xs font-medium rounded-lg disabled:opacity-50 flex items-center gap-1.5`}
          data-testid="slhdsa-keygen-btn"
        >
          {loading === "keygen" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Key className="w-3.5 h-3.5" />}
          1. Generate Key Pair
        </button>
        <button
          onClick={doSign}
          disabled={!keys || !!loading}
          className="px-3 py-1.5 bg-slate-700 text-white text-xs font-medium rounded-lg hover:bg-slate-800 disabled:opacity-50 flex items-center gap-1.5"
          data-testid="slhdsa-sign-btn"
        >
          {loading === "sign" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
          {loading === "sign" ? `Signing... (${(elapsedMs / 1000).toFixed(1)}s)` : "2. Sign"}
        </button>
        <button
          onClick={doVerify}
          disabled={!sig || !!loading}
          className="px-3 py-1.5 bg-slate-700 text-white text-xs font-medium rounded-lg hover:bg-slate-800 disabled:opacity-50 flex items-center gap-1.5"
          data-testid="slhdsa-verify-btn"
        >
          {loading === "verify" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
          3. Verify
        </button>
      </div>

      {/* Progress indicator shown while signing */}
      {loading === "sign" && <SignProgress elapsedMs={elapsedMs} variant={variant} />}

      {keys && (
        <>
          <div className="space-y-1 mt-3">
            <CodeBlock label={`Public Key (${keys.pk_size_bytes}B)`} value={keys.public_key} />
            <CodeBlock label={`Private Key (${keys.sk_size_bytes}B)`} value={keys.private_key} />
          </div>
          <div className="mt-3 flex items-center gap-2">
            <input
              value={keyName}
              onChange={e => setKeyName(e.target.value)}
              placeholder="Key name (optional)"
              className="flex-1 text-xs border border-slate-200 rounded px-2 py-1.5 bg-slate-50 focus:outline-none focus:ring-1 focus:ring-slate-300"
              data-testid="slhdsa-key-name-input"
            />
            <button
              onClick={doSaveToVault}
              disabled={saving}
              className={`px-3 py-1.5 ${info.color} ${info.hoverColor} text-white text-xs font-medium rounded-lg disabled:opacity-50 flex items-center gap-1.5`}
              data-testid="slhdsa-save-vault-btn"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Save to Vault
            </button>
          </div>
        </>
      )}
      {sig && <CodeBlock label={`Signature (${sig.sig_size_bytes}B — ${info.algo})`} value={sig.signature} />}
      {verified !== null && (
        <div className={`mt-3 flex items-center gap-2 text-sm font-semibold ${verified.valid ? "text-emerald-600" : "text-red-600"}`} data-testid="slhdsa-verify-result">
          <CheckCircle2 className="w-4 h-4" />
          {verified.valid ? "Signature valid — message authenticated!" : "Signature INVALID"}
        </div>
      )}
    </div>
  );
}

function VaultAnalyticsPanel({ refreshTrigger }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiGet("/v1/pqc/vault/analytics");
      setAnalytics(d);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load, refreshTrigger]);

  const ALGO_COLORS = {
    "ML-KEM-768":         "#3b82f6",
    "ML-DSA-65":          "#7c3aed",
    "SLH-DSA-SHAKE-256s": "#059669",
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm mb-5" data-testid="vault-analytics-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BarChart2 className="w-4 h-4 text-indigo-600" />
          <h2 className="text-sm font-semibold text-slate-800">Key Vault Analytics</h2>
          {analytics && (
            <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-full font-medium">
              {analytics.total} total key{analytics.total !== 1 ? "s" : ""}
            </span>
          )}
        </div>
        <button onClick={load} className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1" data-testid="refresh-analytics-btn">
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-24 text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading analytics...
        </div>
      ) : !analytics || analytics.total === 0 ? (
        <div className="flex flex-col items-center justify-center h-24 text-slate-400">
          <BarChart2 className="w-7 h-7 mb-2 opacity-30" />
          <p className="text-sm">No keys in vault yet</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Daily Activity Chart */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-2">Daily Key Generation (Last 14 Days)</div>
            <ResponsiveContainer width="100%" height={140}>
              <BarChart data={analytics.daily_activity} margin={{ top: 0, right: 0, left: -25, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 9, fill: "#94a3b8" }} axisLine={false} tickLine={false} interval={2} />
                <YAxis allowDecimals={false} tick={{ fontSize: 9, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ fontSize: 11, border: "1px solid #e2e8f0", borderRadius: 4, padding: "4px 8px" }}
                  formatter={(v) => [v, "Keys"]}
                />
                <Bar dataKey="count" fill="#6366f1" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Algorithm Distribution */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-2">Algorithm Distribution</div>
            {analytics.by_algorithm.length === 0 ? (
              <p className="text-xs text-slate-400">No data</p>
            ) : (
              <div className="space-y-2 mt-3">
                {analytics.by_algorithm.map(({ algorithm, count }) => {
                  const pct = analytics.total > 0 ? Math.round((count / analytics.total) * 100) : 0;
                  const color = ALGO_COLORS[algorithm] || "#94a3b8";
                  return (
                    <div key={algorithm} data-testid={`algo-bar-${algorithm}`}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-slate-600 font-medium">{algorithm}</span>
                        <span className="font-mono text-slate-500">{count} ({pct}%)</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{ width: `${pct}%`, backgroundColor: color }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function KeyVaultPanel({ refreshTrigger }) {
  const [keys, setKeys]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiGet("/v1/pqc/vault/keys");
      setKeys(d.keys || []);
    } catch (e) {
      toast.error("Failed to load vault: " + e.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load, refreshTrigger]);

  const handleDelete = async (keyId) => {
    if (!window.confirm("Delete this key? This cannot be undone.")) return;
    setDeleting(keyId);
    try {
      await apiDelete(`/v1/pqc/vault/keys/${keyId}`);
      toast.success("Key deleted");
      load();
    } catch (e) {
      toast.error(e.message);
    }
    setDeleting(null);
  };

  const algoColor = (algo) => algo === "ML-KEM-768" ? "bg-blue-100 text-blue-700 border-blue-200" : "bg-violet-100 text-violet-700 border-violet-200";

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm" data-testid="key-vault-panel">
      <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Vault className="w-4 h-4 text-emerald-600" />
          <h2 className="text-sm font-semibold text-slate-800">Secure Key Vault</h2>
          <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-full font-medium">
            {keys.length} key{keys.length !== 1 ? "s" : ""}
          </span>
        </div>
        <button
          onClick={load}
          className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1"
          data-testid="refresh-vault-btn"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-24 text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading vault...
        </div>
      ) : keys.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-32 text-slate-400">
          <Vault className="w-8 h-8 mb-2 opacity-40" />
          <p className="text-sm">No keys saved yet</p>
          <p className="text-xs mt-1">Generate a key pair above and click "Save to Vault"</p>
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {keys.map(k => (
            <div key={k.id} className="px-5 py-3 flex items-start gap-3 hover:bg-slate-50 transition-colors" data-testid={`vault-key-${k.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-slate-800 truncate">{k.name}</span>
                  <span className={`text-[10px] font-semibold border px-1.5 py-0.5 rounded-full ${algoColor(k.algorithm)}`}>{k.algorithm}</span>
                </div>
                <div className="flex items-center gap-3 text-[10px] text-slate-400">
                  <span>Saved {new Date(k.created_at).toLocaleString()}</span>
                </div>
                <div className="mt-1.5 space-y-1">
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] text-slate-400 w-16 shrink-0">Public key</span>
                    <code className="text-[10px] font-mono text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded truncate max-w-sm">
                      {k.public_key.slice(0, 48)}...
                    </code>
                    <CopyButton text={k.public_key} />
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] text-slate-400 w-16 shrink-0">Private key</span>
                    <code className="text-[10px] font-mono text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">
                      ••••••••••••••••••••
                    </code>
                    <CopyButton text={k.private_key} />
                  </div>
                </div>
              </div>
              <button
                onClick={() => handleDelete(k.id)}
                disabled={deleting === k.id}
                className="shrink-0 p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                title="Delete key"
                data-testid={`delete-vault-key-${k.id}`}
              >
                {deleting === k.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              </button>
            </div>
          ))}
        </div>
      )}
      {keys.length > 0 && (
        <div className="px-5 py-2 border-t border-slate-100 bg-amber-50 flex items-start gap-2">
          <AlertCircle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-[10px] text-amber-700">
            Private keys are stored as-is for this demo. In production, encrypt private keys at rest using AES-256-GCM before storage.
          </p>
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function PQCPage() {
  const [vaultRefresh, setVaultRefresh] = useState(0);
  const triggerVaultRefresh = () => setVaultRefresh(v => v + 1);

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="pqc-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <ShieldCheck className="w-6 h-6 text-violet-600" />
          Post-Quantum Cryptography
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          NIST-standardized algorithms: ML-KEM-768 (FIPS 203) + ML-DSA-65 (FIPS 204) — quantum-resistant key exchange and digital signatures
        </p>
      </div>

      {/* Info cards */}
      <div className="grid grid-cols-3 gap-3 text-xs mb-5">
        {[
          { label: "ML-KEM-768", detail: "1184B ek / 1088B ct / 32B ss", note: "FIPS 203 · NIST Level 3", color: "blue" },
          { label: "ML-DSA-65",  detail: "1952B pk / 3309B sig",          note: "FIPS 204 · NIST Level 3", color: "violet" },
          { label: "SLH-DSA-SHAKE-256s", detail: "64B pk / 128B sk / ~29KB sig", note: "FIPS 205 · NIST Level 5", color: "emerald" },
          { label: "SLH-DSA-SHAKE-128s", detail: "32B pk / 64B sk / ~7.9KB sig", note: "FIPS 205 · NIST Level 1 (fast)", color: "sky" },
        ].map(({ label, detail, note, color }) => (
          <div key={label} className={`bg-${color}-50 border border-${color}-200 rounded-lg p-3`}>
            <p className={`font-semibold text-${color}-800`}>{label}</p>
            <p className={`text-${color}-600 mt-0.5 font-mono`}>{detail}</p>
            <p className={`text-${color}-500 mt-0.5`}>{note}</p>
          </div>
        ))}
      </div>

      {/* Algorithm panels */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        <MLKEMPanel onSaved={triggerVaultRefresh} />
        <MLDSAPanel onSaved={triggerVaultRefresh} />
      </div>

      {/* SLH-DSA panel (full width) */}
      <div className="mb-5">
        <SLHDSAPanel onSaved={triggerVaultRefresh} />
      </div>

      {/* Key Vault Analytics */}
      <VaultAnalyticsPanel refreshTrigger={vaultRefresh} />

      {/* Key Vault */}
      <KeyVaultPanel refreshTrigger={vaultRefresh} />
    </div>
  );
}
